import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import CheckoutSectionHeader from "@tSurya/Components/CheckoutSectionHeader";
import { getDisableCheckoutContinueButtonForPickUp, getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import InfoCircleIcon from "@tSurya/Icons/InfoCircleIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import React, { FC } from "react";
import { css } from "styled-components";

export interface PickUpAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    findLocationLink?: LinkPresentationProps;
    selectedLocationGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoDisplayStyles;
    scheduleNotice?: TypographyPresentationProps;
    findLocationModal?: FindLocationModalStyles;
    cardContentGridItem?: GridItemProps;
    cardContent?: InjectableCss;
}

export const pickUpAddressStyles: PickUpAddressStyles = {
    container: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    findLocationLink: {
        css: css`
            margin-left: 20px;
        `,
    },
    selectedLocationGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    cardContentGridItem: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    cardContent: {
        css: css`
            background-color: ${COLORS.white};
            border: 1px solid ${COLORS.border.main};
            border-radius: 4px;
            padding: 20px;
        `,
    },
    scheduleNotice: {
        css: css`
            display: block;
            margin-top: 8px;
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${COLORS.checkout?.text?.secondary ?? COLORS.gray["600"]};

            svg {
                vertical-align: text-bottom;
                margin-right: 0.375em;
            }
        `,
    },
};

const PickUpAddress: FC<{
    address?: WarehouseModel;
    onChange?: (address: WarehouseModel) => void;
    extendedStyles?: PickUpAddressStyles;
}> = ({ address, onChange, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(pickUpAddressStyles, extendedStyles));
    const isUkWebsite = useAppSelector(getIsUkWebsite);
    const disableCheckoutContinueButtonForPickUp = useAppSelector(getDisableCheckoutContinueButtonForPickUp);
    const showPickUpAddress = isUkWebsite && disableCheckoutContinueButtonForPickUp;

    return (
        <GridContainer
            {...styles.container}
            css={showPickUpAddress ? pickUpAddressStyles.container?.css : styles.container?.css}
            data-test-selector="checkoutShipping_pickUpAddress"
        >
            <CheckoutSectionHeader title={translate("Pick Up from Warehouse")} />
            {address && (
                <GridItem {...styles.cardContentGridItem}>
                    <StyledWrapper {...styles.cardContent}>
                        <GridItem {...styles.selectedLocationGridItem}>
                            {isUkWebsite ? (
                                siteMessage("PickUpfromWarehouse")
                            ) : (
                                <>
                                    <Typography
                                        {...styles.selectedLocationName}
                                        data-test-selector="checkoutShipping_pickUpAddress_warehouseName"
                                    >
                                        {address.description || address.name}
                                    </Typography>
                                    <AddressInfoDisplay
                                        {...address}
                                        extendedStyles={styles.selectedLocationAddressDisplay}
                                    />
                                    {/* Pickup must be scheduled; the address alone could suggest a customer can just walk in. */}
                                    <Typography {...styles.scheduleNotice}>
                                        <InfoCircleIcon />
                                        {translate(
                                            "Pickup must be scheduled in advance. Please contact Customer Support at 877.275.7847 or info@suryainc.com to arrange your pickup time.",
                                        )}
                                    </Typography>
                                </>
                            )}
                        </GridItem>
                    </StyledWrapper>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default PickUpAddress;
